import React, { createContext, useContext, useEffect, useState } from 'react';
import jwtAuthService from './jwtAuthService.js';

// Create the authentication context
const JWTAuthContext = createContext();

// Custom hook to use the authentication context
export const useJWTAuth = () => {
    const context = useContext(JWTAuthContext);
    if (!context) {
        throw new Error('useJWTAuth must be used within a JWTAuthProvider');
    }
    return context;
};

// JWT Authentication Provider Component
export const JWTAuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isMobileVerified, setIsMobileVerified] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    // Check authentication status on app start
    useEffect(() => {
        const initializeAuth = async () => {
            try {
                const authenticated = await jwtAuthService.isAuthenticated();
                setIsAuthenticated(authenticated);
                
                if (authenticated) {
                    const currentUser = jwtAuthService.getCurrentUser();
                    setUser(currentUser);
                    // Set mobile verification status
                    const mobileVerified = sessionStorage.getItem('wiziai_mobile_verified') === 'true';
                    setIsMobileVerified(mobileVerified);
                }
            } catch (error) {
                console.error('Auth initialization failed:', error);
                setIsAuthenticated(false);
                setUser(null);
                setIsMobileVerified(false);
            } finally {
                setIsLoading(false);
            }
        };

        initializeAuth();
    }, []);

    // Login function
    const login = async (credentials) => {
        try {
            setIsLoading(true);
            console.log('JWT Login attempt started...');
            const result = await jwtAuthService.login(credentials);
            
            console.log('JWT Login result:', result);
            
            if (result.success) {
                console.log('Login successful, updating auth state...');
                setIsAuthenticated(true);
                const currentUser = jwtAuthService.getCurrentUser();
                setUser(currentUser);
                // Update mobile verification status
                const mobileVerified = sessionStorage.getItem('wiziai_mobile_verified') === 'true';
                setIsMobileVerified(mobileVerified);
                console.log('Auth state updated - isAuthenticated: true, isMobileVerified:', mobileVerified, 'user:', currentUser);
                return { success: true, message: result.message };
            } else {
                console.log('Login failed:', result.message);
                setIsAuthenticated(false);
                setUser(null);
                setIsMobileVerified(false);
                return { success: false, message: result.message };
            }
        } catch (error) {
            console.error('Login failed:', error);
            setIsAuthenticated(false);
            setUser(null);
            return { success: false, message: error.message || 'Login failed' };
        } finally {
            setIsLoading(false);
            console.log('Login process completed');
        }
    };

    // Logout function
    const logout = async () => {
        try {
            await jwtAuthService.logout();
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            // Clear localStorage user data as well
            localStorage.removeItem('user');
            setIsAuthenticated(false);
            setUser(null);
            setIsMobileVerified(false);
        }
    };

    // Refresh user data
    const refreshUser = () => {
        const currentUser = jwtAuthService.getCurrentUser();
        setUser(currentUser);
    };

    // Set user and tokens directly (for OAuth flows)
    const setAuthData = (userData, accessToken, refreshToken, expiresIn = 3600, refreshExpiresIn = 604800) => {
        try {
            // Store tokens using the same method as jwtAuthService
            const tokens = {
                access_token: accessToken,
                refresh_token: refreshToken,
                expires_in: expiresIn,
                refresh_expires_in: refreshExpiresIn
            };
            
            // Use jwtAuthService to store tokens properly
            jwtAuthService.setTokens(tokens);
            
            // Store user data in localStorage for backward compatibility
            localStorage.setItem('user', JSON.stringify(userData));
            
            // ✅ CRITICAL FIX: Store individual user fields in sessionStorage 
            // This ensures getCurrentUser() works properly after page refresh
            sessionStorage.setItem('wiziai_username', userData.username || '');
            sessionStorage.setItem('wiziai_display_name', userData.display_name || userData.username || '');
            sessionStorage.setItem('wiziai_user_id', String(userData.id || ''));
            sessionStorage.setItem('wiziai_email', userData.email || userData.username || '');
            
            // Store mobile verification status (check userData for the flag)
            const mobileVerified = userData.mobile_verified === true || userData.mobile_verified === 1 || userData.mobile_verified === '1';
            sessionStorage.setItem('wiziai_mobile_verified', mobileVerified.toString());
            
            // Update state
            setUser(userData);
            setIsAuthenticated(true);
            setIsMobileVerified(mobileVerified);
            
            return { success: true };
        } catch (error) {
            console.error('Failed to set auth data:', error);
            return { success: false, message: error.message };
        }
    };

    // Refresh authentication data (useful after mobile verification)
    const refreshAuthData = async () => {
        try {
            const authenticated = await jwtAuthService.isAuthenticated();
            if (authenticated) {
                const currentUser = jwtAuthService.getCurrentUser();
                setUser(currentUser);
                setIsAuthenticated(true);
                // Update mobile verification status
                const mobileVerified = sessionStorage.getItem('wiziai_mobile_verified') === 'true';
                setIsMobileVerified(mobileVerified);
                console.log('Auth data refreshed - isMobileVerified:', mobileVerified);
                return { success: true };
            } else {
                setIsAuthenticated(false);
                setUser(null);
                setIsMobileVerified(false);
                return { success: false };
            }
        } catch (error) {
            console.error('Failed to refresh auth data:', error);
            return { success: false, message: error.message };
        }
    };

    // Context value
    const value = {
        user,
        isAuthenticated,
        isMobileVerified,
        isLoading,
        login,
        logout,
        refreshUser,
        setAuthData,
        refreshAuthData
    };

    return (
        <JWTAuthContext.Provider value={value}>
            {children}
        </JWTAuthContext.Provider>
    );
};

export default JWTAuthContext;
